import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Sparkle, Users, Copy, Check, Crown, ArrowLeft } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Circle() {
    const { sessionId } = useParams();
    const [state, setState] = useState("loading");
    const [circle, setCircle] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        axios
            .get(`${API}/circle/${sessionId}`)
            .then((r) => {
                setCircle(r.data);
                setState("ok");
            })
            .catch(() => setState("error"));
    }, [sessionId]);

    const inviteUrl = circle ? `${window.location.origin}/invite/${circle.invite_code}` : "";
    const copyInvite = async () => {
        try {
            await navigator.clipboard.writeText(inviteUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {}
    };

    return (
        <main data-testid="circle-page" className="min-h-screen bg-cream text-navy">
            <header className="border-b border-warmgray bg-cream/80 backdrop-blur-xl">
                <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-5">
                    <Link to="/" data-testid="circle-logo" aria-label="Ago GenApp home">
                        <img src="/logo.png" alt="Ago GenApp" className="h-10 w-auto" />
                    </Link>
                    <Link
                        to="/"
                        data-testid="circle-back-home"
                        className="flex items-center gap-2 rounded-full border-2 border-navy px-5 py-2 text-sm font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5"
                    >
                        <ArrowLeft size={15} /> Back Home
                    </Link>
                </div>
            </header>

            <div className="mx-auto max-w-4xl px-6 py-16 md:py-20">
                {state === "loading" && (
                    <div className="flex justify-center py-20">
                        <Loader2 size={32} className="animate-spin text-lavender" />
                    </div>
                )}

                {state === "error" && (
                    <div data-testid="circle-error" className="rounded-3xl border-2 border-warmgray bg-ivory p-10 text-center">
                        <h1 className="font-display text-2xl md:text-3xl">We couldn’t find this circle.</h1>
                        <p className="mt-3 text-sm text-navy">
                            This link comes from your payment confirmation — check that you copied the full address from your success page or email.
                        </p>
                    </div>
                )}

                {state === "ok" && circle && (
                    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}>
                        <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.35em] text-[#C98A94]">Your Founding Circle</p>
                        <h1 data-testid="circle-heading" className="font-display text-4xl tracking-tight md:text-5xl">
                            Welcome, <span className="italic">Founding Family.</span>
                        </h1>
                        <p className="mt-4 max-w-2xl text-base leading-relaxed text-navy">{circle.email}</p>

                        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
                            <div data-testid="circle-tier-card" className="rounded-3xl border-2 border-warmgray bg-ivory p-7">
                                <div className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C98A94]">
                                    <Crown size={14} /> Your tier
                                </div>
                                <p className="mt-3 font-display text-3xl">{circle.tier_name}</p>
                                <p className="mt-2 text-sm text-navy">
                                    ${circle.amount_paid?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    {circle.is_recurring ? " · monthly" : " · one-time"} contribution
                                </p>
                            </div>
                            <div data-testid="circle-family-count" className="rounded-3xl border-2 border-warmgray bg-ivory p-7">
                                <div className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C98A94]">
                                    <Users size={14} /> Family joined
                                </div>
                                <p className="mt-3 font-display text-3xl">{circle.invite_uses}</p>
                                <p className="mt-2 text-sm text-navy">
                                    {circle.invite_uses === 0 ? "Share your link below to bring your family in" : "family members accepted your invitation"}
                                </p>
                            </div>
                        </div>

                        <div data-testid="circle-invite-card" className="mt-6 rounded-3xl border-2 border-warmgray bg-ivory p-7">
                            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C98A94]">
                                Your invite link
                            </p>
                            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                                <p data-testid="circle-invite-link" className="flex-1 break-all rounded-xl bg-[#FDFBF9] px-4 py-3 font-mono text-xs text-navy">
                                    {inviteUrl}
                                </p>
                                <button
                                    data-testid="circle-copy-button"
                                    onClick={copyInvite}
                                    className="btn-gradient flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                                >
                                    {copied ? <Check size={15} /> : <Copy size={15} />}
                                    {copied ? "Copied!" : "Copy"}
                                </button>
                            </div>
                        </div>

                        <div className="mt-6 rounded-3xl border-2 border-warmgray bg-ivory p-7">
                            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C98A94]">
                                Family in your circle
                            </p>
                            {circle.family.length === 0 ? (
                                <p data-testid="circle-family-empty" className="mt-4 text-sm leading-relaxed text-navy">
                                    No one has joined yet. Send your invite link to the people whose stories you never want to lose.
                                </p>
                            ) : (
                                <ul data-testid="circle-family-list" className="mt-4 divide-y divide-warmgray">
                                    {circle.family.map((f) => (
                                        <li key={f.email} className="flex items-center justify-between py-3">
                                            <span className="text-sm font-medium text-navy">{f.email}</span>
                                            <span className="font-mono text-[11px] text-[#2C3347]">
                                                {f.created_at ? new Date(f.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : ""}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </motion.div>
                )}
            </div>
        </main>
    );
}
